import React from "react";
import { Settings, BarChart3, Filter, Calendar } from "lucide-react";
import SearchBar from "../../../../components/SearchBar";
import ReusableDropdown from "../../../../components/ReusableDropdown";

const PayrollFilters = ({
  t,
  styles,
  currentTheme,
  theme,
  language,
  payrollData,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  statusSearch,
  setStatusSearch,
  selectedDepartment,
  setSelectedDepartment,
  departmentSearch,
  setDepartmentSearch,
  selectedCompany,
  setSelectedCompany,
  companySearch,
  setCompanySearch,
  selectedLaborMode,
  setSelectedLaborMode,
  laborModeSearch,
  setLaborModeSearch,
  selectedMonth,
  setSelectedMonth,
  yearSearch,
  setYearSearch,
  monthSearch,
  setMonthSearch,
  filteredCount,
  totalCount,
  actionButtons,
}) => {
  const isRTL = language === "ar";

  // ── Shared label style (same as EmployeeTable / Attendance) ──
  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    flexDirection: "row",
    textAlign: isRTL ? "right" : "left",
  };

  // Derive department list from payroll data
  const departments = Array.from(
    new Set((payrollData || []).map((p) => p.department).filter(Boolean))
  ).sort();

  // Derive company list from payroll data
  const companies = Array.from(
    new Set((payrollData || []).map((p) => p.workingCompany).filter(Boolean))
  ).sort();

  return (
    <div>
      {/* ── Header row (same as Attendance/EmployeeTable FiltersPanel) ───── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "16px",
          flexDirection: isRTL ? "row" : "row",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "clamp(16px, 3vw, 20px)",
              fontWeight: "700",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <Settings size={20} />
            {isRTL ? "الفلاتر والإجراءات" : "Filters & Actions"}
          </h3>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <BarChart3 size={16} />
            {isRTL ? "عرض" : "Showing"}{" "}
            {typeof filteredCount !== "undefined" ? filteredCount : ""}{" "}
            {isRTL ? "من" : "of"}{" "}
            {typeof totalCount !== "undefined" ? totalCount : ""}{" "}
            {isRTL ? "سجلات" : "records"}
          </div>
        </div>
      </div>

      {/* ── Filter inputs grid (same as EmployeeTable) ────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
        }}
      >
        {/* Search */}
        <div>
          <label style={labelStyle}>
            {t("searchEmployees")}
          </label>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("searchPlaceholder")}
            theme={theme}
            language={language}
            icon={true}
          />
        </div>

        {/* Status */}
        <div>
          <label style={labelStyle}>
            <Filter size={16} />
            {t("paymentStatus")}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "All", label: t("allStatuses") },
                { key: "paid", label: t("paid") },
                { key: "pending", label: t("pending") },
                { key: "approved", label: t("approved") },
                { key: "rejected", label: t("rejected") },
              ]}
              value={statusSearch}
              onChange={(val) => setStatusSearch(val)}
              onSelect={(item) => {
                setStatusFilter(item.key);
                setStatusSearch(item.label);
              }}
              onClear={() => {
                setStatusSearch("");
                setStatusFilter("All");
              }}
              placeholder={t("allStatuses")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Department */}
        <div>
          <label style={labelStyle}>
            <Filter size={16} />
            {t("department")}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "All", label: isRTL ? "الكل" : "All" },
                ...departments.map((dep) => ({ key: dep, label: dep })),
              ]}
              value={departmentSearch}
              onChange={(val) => setDepartmentSearch(val)}
              onSelect={(item) => {
                setSelectedDepartment(item.key);
                setDepartmentSearch(item.label);
              }}
              onClear={() => {
                setDepartmentSearch("");
                setSelectedDepartment("All");
              }}
              placeholder={isRTL ? "الكل" : "All"}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={8}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Company */}
        <div>
          <label style={labelStyle}>
            <Filter size={16} />
            {t("company") || (isRTL ? "الشركة" : "Company")}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "All", label: isRTL ? "الكل" : "All" },
                ...companies.map((c) => ({ key: c, label: c })),
              ]}
              value={companySearch}
              onChange={(val) => setCompanySearch(val)}
              onSelect={(item) => {
                setSelectedCompany(item.key);
                setCompanySearch(item.label);
              }}
              onClear={() => {
                setCompanySearch("");
                setSelectedCompany("All");
              }}
              placeholder={isRTL ? "الكل" : "All"}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={8}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Labor Mode (Insource / Outsource) */}
        <div>
          <label style={labelStyle}>
            <Filter size={16} />
            {t("laborMode")}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "All", label: t("allLaborModes") },
                { key: "Insource", label: t("insource") },
                { key: "Outsource", label: t("outsource") },
              ]}
              value={laborModeSearch}
              onChange={(val) => setLaborModeSearch(val)}
              onSelect={(item) => {
                setSelectedLaborMode(item.key);
                setLaborModeSearch(item.label);
              }}
              onClear={() => {
                setLaborModeSearch("");
                setSelectedLaborMode("All");
              }}
              placeholder={t("allLaborModes")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Month */}
        <div>
          <label style={labelStyle}>
            <Calendar size={16} />
            {t("payPeriodMonth")}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: 1, label: t("january") },
                { key: 2, label: t("february") },
                { key: 3, label: t("march") },
                { key: 4, label: t("april") },
                { key: 5, label: t("may") },
                { key: 6, label: t("june") },
                { key: 7, label: t("july") },
                { key: 8, label: t("august") },
                { key: 9, label: t("september") },
                { key: 10, label: t("october") },
                { key: 11, label: t("november") },
                { key: 12, label: t("december") },
              ]}
              value={monthSearch}
              onChange={(val) => setMonthSearch(val)}
              onSelect={(item) => {
                setSelectedMonth({ ...selectedMonth, month: item.key });
                setMonthSearch(item.label);
              }}
              onClear={() => {
                setMonthSearch("");
                setSelectedMonth({
                  ...selectedMonth,
                  month: new Date().getMonth() + 1,
                });
              }}
              placeholder={t("selectMonth")}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={12}
              showAllOnArrowClick={true}
            />
          </div>
        </div>
      </div>

      {/* ── Action Buttons ─────────────────────────────────────────────── */}
      {actionButtons && (
        <div style={{ marginTop: "20px" }}>
          {actionButtons}
        </div>
      )}
    </div>
  );
};

export default PayrollFilters;